import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import { toast } from "react-toastify";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import Preloader from "../../shared/PreLoaders";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const fieldWrap = { marginBottom: 16 };

const EditEcoTourism = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const Api = process.env.REACT_APP_API;
  const { userId } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [ranges, setRanges] = useState([]);
  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [vfcs, setVfcs] = useState([]);

  const [formData, setFormData] = useState({
    ecoTourismName: "",
    ecoTourismLocation: "",
    rangeId: "",
    jfmcType: "",
    vfcId: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });

  useEffect(() => {
    fetchEcoTourism();
    fetchRanges();
    fetchJfmcTypes();
  }, []);

  useEffect(() => {
    if (formData.rangeId && formData.jfmcType) {
      fetchVfcs(formData.rangeId, formData.jfmcType);
    }
  }, [formData.rangeId, formData.jfmcType]);

  const fetchEcoTourism = async () => {
    try {
      setLoading(true);
      const config = Authorization();
      const res = await axios.get(`${Api}/api/eco-tourism/${id}`, config);
      const eco = res.data.data;
      setFormData({
        ecoTourismName: eco.ecoTourismName,
        ecoTourismLocation: eco.ecoTourismLocation,
        rangeId: eco.rangeId?._id,
        jfmcType: eco.jfmcType?._id,
        vfcId: eco.vfcId?._id,
        accountNumber: eco.accountNumber,
        ifscCode: eco.ifscCode,
        bankName: eco.bankName,
      });
    } catch (error) {
      toast.error("Failed to load Eco Tourism data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRanges = async () => {
    const config = Authorization();
    const res = await axios.get(`${Api}/api/ranges`, config);
    setRanges(res.data || []);
  };

  const fetchJfmcTypes = async () => {
    const config = Authorization();
    const res = await axios.get(`${Api}/api/jfmc-Types`, config);
    setJfmcTypes(res.data || []);
  };

  const fetchVfcs = async (rangeId, jfmcTypeId) => {
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/vfcs/vfcs-by-range`, { rangeId, jfmcTypeId }, config);
      setVfcs(res.data.vfcs || []);
    } catch {
      setVfcs([]);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const config = Authorization();
      await axios.put(`${Api}/api/eco-tourism/${id}`, formData, config);
      toast.success("Eco Tourism updated successfully");
      await logActivity(userId, "Edited Eco Tourism", "Edit Eco Tourism Page", Api);
      navigate("/eco-tourism/manage");
    } catch (error) {
      toast.error(error.response?.data?.error || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = (e) => (e.target.style.borderColor = "#4CAF50");
  const blurStyle = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit Eco Tourism</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit Eco Tourism</span>
        </div>
      </div>

      {/* ── Form Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
            <i className="mdi mdi-pine-tree" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Edit Eco Tourism Details</span>
        </div>

        <div style={{ padding: "24px 28px" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={fieldWrap}>
                <label htmlFor="ecoTourismName" style={labelStyle}>Eco Name</label>
                <input id="ecoTourismName" type="text" value={formData.ecoTourismName}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="ecoTourismLocation" style={labelStyle}>Location</label>
                <input id="ecoTourismLocation" type="text" value={formData.ecoTourismLocation}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="rangeId" style={labelStyle}>Range</label>
                <select id="rangeId" value={formData.rangeId} onChange={handleChange} required
                  style={{ ...inputStyle, cursor: "pointer" }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select Range</option>
                  {ranges.map((r) => (
                    <option key={r._id} value={r._id}>{r.rangeName}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrap}>
                <label htmlFor="jfmcType" style={labelStyle}>JFMC Type</label>
                <select id="jfmcType" value={formData.jfmcType} onChange={handleChange} required
                  style={{ ...inputStyle, cursor: "pointer" }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select JFMC Type</option>
                  {jfmcTypes.map((j) => (
                    <option key={j._id} value={j._id}>{j.jfmcTypeName}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrap}>
                <label htmlFor="vfcId" style={labelStyle}>VFC</label>
                <select id="vfcId" value={formData.vfcId} onChange={handleChange} required
                  style={{ ...inputStyle, cursor: "pointer" }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select VFC</option>
                  {vfcs.map((v) => (
                    <option key={v._id} value={v._id}>{v.vfcName}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrap}>
                <label htmlFor="bankName" style={labelStyle}>Bank Name</label>
                <input id="bankName" type="text" value={formData.bankName}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="accountNumber" style={labelStyle}>Account Number</label>
                <input id="accountNumber" type="text" value={formData.accountNumber}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="ifscCode" style={labelStyle}>IFSC Code</label>
                <input id="ifscCode" type="text" value={formData.ifscCode}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button type="submit" disabled={loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", opacity: loading ? 0.7 : 1 }}>
                <i className="mdi mdi-content-save" /> {loading ? "Saving..." : "Update"}
              </button>
              <button type="button" onClick={() => navigate(-1)}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditEcoTourism;
